import React, { useState, useEffect } from 'react';
import { Tab, Nav } from 'react-bootstrap';
import { toast } from 'react-toastify';
import MasterService from '../../services/MasterService';
import './ReconList.scss';
import ReconListFilter from './ReconListFilter';
import BankMatchRecordPopupList from './BankMatchRecordPopupList';
import LoanAssignmentRecordPopup from './LoanAssignmentRecordPopup';
import StatusHistoryPopup from './StatusHistoryPopup';
const ReconList = () => {
    const [data, setData] = useState([]);
    const [counts, setCounts] = useState({ new: 0, pending: 0, completed: 0, total: 0 });
    const [pagination, setPagination] = useState({
        totalpage: 1,
        totalrecords: 0,
        prevpage: false,
        nextpage: false,
        page_size: 10
    });
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [modalMode, setModalMode] = useState('edit'); // 'edit' or 'view'
    const [filters, setFilters] = useState({});
    const [activeFilters, setActiveFilters] = useState([]);

    // Fetch data from API
    const fetchData = async (filterParams = {}, page = 1) => {
        setLoading(true);
        setError(null);

        const params = {
            ...filterParams,
            page_no: page,
            page_size: pagination.page_size
        };

        try {
            const response = await MasterService.getReconciliationList(params);

            if (response) {
                setData(response.data || []);
                setCounts(response.counts || { new: 0, pending: 0, completed: 0, total: 0 });
                if (response.pagination) {
                    setPagination(response.pagination);
                }
            }
        } catch (err) {
            console.error('Error fetching data:', err);
            setError(err.message || 'Failed to fetch data');
        } finally {
            setLoading(false);
        }
    };

    // Initial and filtered data fetch
    useEffect(() => {
        fetchData(filters, currentPage);
    }, [currentPage, filters]);

    // Handle page change
    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.totalpage) {
            setCurrentPage(newPage);
        }
    };

    // Handle filter application
    const handleApplyFilters = (newFilters) => {
        setFilters(newFilters);
        setCurrentPage(1); // Reset to first page on new filter

        // Create active filter chips
        const chips = [];
        Object.keys(newFilters).forEach(key => {
            if (newFilters[key]) {
                chips.push({
                    key,
                    label: formatFilterLabel(key, newFilters[key]),
                    value: newFilters[key]
                });
            }
        });
        setActiveFilters(chips);
    };

    // Remove filter chip
    const removeFilter = (filterKey) => {
        const newFilters = { ...filters };
        delete newFilters[filterKey];
        setFilters(newFilters);
        setCurrentPage(1);

        const newChips = activeFilters.filter(chip => chip.key !== filterKey);
        setActiveFilters(newChips);
    };

    // Clear all filters
    const clearAllFilters = () => {
        setFilters({});
        setActiveFilters([]);
        setCurrentPage(1);
    };

    // Format filter label for chips
    const formatFilterLabel = (key, value) => {
        const labels = {
            dealer_id: `Dealer ID: ${value}`,
            dealer_name: `Dealer: ${value}`,
            overall_status: `Status: ${getStatusLabel(value)}`,
            bank_match_status: `Bank Match: ${getBankMatchLabel(value)}`,
            loan_assigned_status: `Loan Status: ${getLoanStatusLabel(value)}`,
            start_date: `From: ${value}`,
            end_date: `To: ${value}`
        };
        return labels[key] || `${key}: ${value}`;
    };

    // Get status labels
    const getStatusLabel = (status) => {
        const labels = { '1': 'New', '2': 'Pending', '3': 'Completed' };
        return labels[status] || status;
    };

    const getBankMatchLabel = (status) => {
        const labels = { '0': 'No action', '1': 'Partial Match', '2': 'System Match', '3': 'Manual Match', '4': 'Not Matched' };
        return labels[status] || status;
    };

    const getLoanStatusLabel = (status) => {
        const labels = { '0': 'No action', '1': 'Partial Match', '2': 'System Match', '3': 'Manual Match', '4': 'Not Matched' };
        return labels[status] || status;
    };

    // Format currency
    const formatCurrency = (amount) => {
        if (!amount || amount === '0.00' || amount === 0) return '0';
        return `Rp ${parseFloat(amount).toLocaleString('id-ID')}`;
    };

    // Format date
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

      const formatDateOnly = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    // Show modal for edit
    const handleEditDetails = async (record) => {
        setSelectedRecord(record);
        setModalMode('edit');
        setLoading(true);
        try {
            const response = await MasterService.getReconciliationDetails(record.id);
            if (response && response.data) {
                setSelectedRecord(response.data);
                setShowModal(true);
                document.body.classList.add("overflow-hidden");
            }
        } catch (err) {
            console.error('Error fetching details:', err);
            setError('Failed to fetch record details');
        } finally {
            setLoading(false);
        }
    };

    // Show modal for view
    const handleViewDetails = async (record) => {
        setSelectedRecord(record);
        setModalMode('view');
        setLoading(true);
        try {
            const response = await MasterService.getReconciliationDetails(record.id);
            if (response && response.data) {
                setSelectedRecord(response.data);
                setShowModal(true);
                document.body.classList.add("overflow-hidden");
            }
        } catch (err) {
            console.error('Error fetching details:', err);
            setError('Failed to fetch record details');
        } finally {
            setLoading(false);
        }
    };

    // Hide modal
    const hideModal = () => {
        setShowModal(false);
        setShowHistoryModal(false);
        setSelectedRecord(null);
        document.body.classList.remove("overflow-hidden");
    };

    // Show status history popup
    const handleViewHistory = (record) => {
        setSelectedRecord(record);
        setShowHistoryModal(true);
        document.body.classList.add("overflow-hidden");
    };

    // Handle individual record reconciliation
    const handleReconcile = async (recordId) => {
        if (!window.confirm("Are you sure you want to reconcile this record?")) {
            return;
        }

        setLoading(true);
        try {
            const response = await MasterService.reconcileUploadProofMatch([recordId]);
            if (response) {
                // MasterService handles the success toast
                fetchData(filters, currentPage);
            }
        } catch (err) {
            console.error('Error initiating reconciliation:', err);
            // MasterService handles the error toast
        } finally {
            setLoading(false);
        }
    };

    // Handle stat click to filter data
    const handleStatClick = (status) => {
        let newFilters = { ...filters };
        
        if (status === 'total') {
            // Remove overall_status filter if it exists
            delete newFilters.overall_status;
        } else {
            // Set the overall_status filter
            newFilters.overall_status = status;
        }
        
        handleApplyFilters(newFilters);
    };

    // Callback for successful actions in popups
    const handleActionSuccess = async () => {
        // fetchData(filters, currentPage); // Refresh the main table in background
        
        if (selectedRecord) {
            // Re-fetch details to show updated state in modal
            try {
                const response = await MasterService.getReconciliationDetails(selectedRecord.id);
                if (response && response.data) {
                    setSelectedRecord(response.data);
                }
            } catch (err) {
                console.error('Error refreshing details:', err);
            }
        }
        
        // Also refresh the main table in background
        fetchData(filters, currentPage);
    };

    return (
        <React.Fragment>
            <div className="container-fluid">
                {/* Header */}
                <div className="top-heading heading-d-flex">
                    <h1>Reconciliation Module</h1>
                    {/* <div className="reconcile-btn-txt">
                        <button className="btn-primary">
                            <i className="ic-replay m-xs-r"></i>Reconcile
                        </button>
                        <span>Last Reconciled: {formatDate(new Date())}</span>
                    </div> */}
                </div>

                {/* Filters */}
                <div className="loan-filter-bg">
                    <div className="top-filter-bg">
                        <div className="lead-list-filter">
                            <ReconListFilter onApplyFilters={handleApplyFilters} />
                        </div>
                    </div>

                    {/* Filter Chips */}
                    {activeFilters.length > 0 && (
                        <div className="filter-chips-container">
                            <div className="filter-chips">
                                {activeFilters.map((chip) => (
                                    <div key={chip.key} className="filter-chip">
                                        <span>{chip.label}</span>
                                        <button onClick={() => removeFilter(chip.key)}>
                                            <i className="ic-clearclose"></i>
                                        </button>
                                    </div>
                                ))}
                                <button className="clear-all-btn" onClick={clearAllFilters}>
                                    Clear All
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Error Message */}
                {error && (
                    <div className="alert alert-danger" role="alert">
                        {error}
                    </div>
                )}

                {/* Table */}
                <div className="reconciliation-table-outer">
                    <div className="reconciliation-table-heading">
                        Reconciliation Table
                    </div>

                    {/* Stats */}
                    <div className="reconciliation-record-status-list">
                        <ul>
                            <li 
                                className="active clickable-stat" 
                                onClick={() => handleStatClick('total')}
                                title="Show all records"
                            >
                                <span>{counts.total}</span>
                                <label>Total Records</label>
                            </li>
                            <li 
                                className="clickable-stat"
                                onClick={() => handleStatClick('3')}
                                title="Show completed records"
                            >
                                <span className="complete-record-txt">{counts.completed}</span>
                                <label>Completed Records</label>
                            </li>
                            <li 
                                className="clickable-stat"
                                onClick={() => handleStatClick('2')}
                                title="Show pending records"
                            >
                                <span className="pending-record-txt">{counts.pending}</span>
                                <label>Pending Records</label>
                            </li>
                            <li 
                                className="clickable-stat"
                                onClick={() => handleStatClick('1')}
                                title="Show new records"
                            >
                                <span className="new-record-txt">{counts.new}</span>
                                <label>New Records</label>
                            </li>
                        </ul>
                    </div>

                    {/* Loading State */}
                    {loading && (
                        <div className="loading-container text-center p-5">
                            <div className="spinner-border" role="status">
                                <span className="sr-only">Loading...</span>
                            </div>
                        </div>
                    )}

                    {/* Data Table */}
                    {!loading && (
                        <div className="lead-list-data-table">
                            <table>
                                <thead>
                                    <tr>
                                        <th>ID</th>
                                        <th>Dealership Details</th>
                                        <th>Upload Details</th>
                                        <th>Expected Payment</th>
                                        <th>OCR Read</th>
                                        <th>Overall Status</th>
                                        <th>Bank Match Status</th>
                                        <th>Loan Assigned Status</th>
                                        <th>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.length === 0 ? (
                                        <tr>
                                            <td colSpan="9" className="text-center">
                                                No records found
                                            </td>
                                        </tr>
                                    ) : (
                                        data.map((record) => (
                                            <tr key={record.id}>
                                                <td>
                                                    <span>{record.id}</span>
                                                </td>
                                                <td>
                                                    <span>{record.dealerName}</span>
                                                    <span>GCD-{record.dealerId}</span>
                                                </td>
                                                <td>
                                                    <span>{formatDate(record.uploadProofDate)}</span>
                                                    <span>Loan ID: {record.uploadProofLoanId}</span>
                                                </td>
                                                <td>
                                                    <span><b>Repayment</b>: {formatCurrency(record.basicAmount) }</span>
                                                    <span><b>Renewal</b>: {formatCurrency(record.renewalAmount)}</span>
                                                    <span><b>Due Date</b>: {record?.loanDueDate ? formatDateOnly(record.loanDueDate) : "N/A"}</span>
                                                </td>
                                                <td>
                                                    <span>Date: {record.ocrData?.ocrDate && record.ocrData?.ocrTime && record.ocrData?.ocrTime !== "00:00:00" ? formatDate(record.ocrData?.ocrDate+" "+record.ocrData?.ocrTime) : "N/A"}</span>
                                                    <span>Amount: {formatCurrency(record.ocrData?.ocrAmount)}</span>
                                                    <span>Bank: {record.ocrData?.bankName || 'N/A'}</span>
                                                </td>
                                                <td>
                                                    <span className={`status-${record.overallStatus}`}>
                                                        {getStatusLabel(record.overallStatus)}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className={`bank-match-${record.bankMatchStatus}`}>
                                                        {getBankMatchLabel(record.bankMatchStatus)}
                                                        <div className="status-info-wrapper">
                                                            <i className="ic-info-icon1"></i>
                                                            {record.ocrData && (
                                                                <div className="status-tooltip">
                                                                    <div className="tooltip-item">
                                                                        <strong>Bank:</strong>
                                                                        <span>{record.ocrData.bankName || 'N/A'}</span>
                                                                    </div>
                                                                    <div className="tooltip-item">
                                                                        <strong>Date:</strong>
                                                                        <span>{formatDate(record.ocrData.ocrDate)}</span>
                                                                    </div>
                                                                    <div className="tooltip-item">
                                                                        <strong>Amount:</strong>
                                                                        <span>{formatCurrency(record.ocrData.ocrAmount)}</span>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className={`loan-status-${record.loanAssignedStatus}`}>
                                                        {getLoanStatusLabel(record.loanAssignedStatus)}
                                                        <div className="status-info-wrapper">
                                                            <i className="ic-info-icon1"></i>
                                                            {record.loanMatchDetails && record.loanMatchDetails.length > 0 && (
                                                                <div className="status-tooltip">
                                                                    {record.loanMatchDetails.map((loan, idx) => (
                                                                        <div key={idx} className={idx > 0 ? "mt-2 pt-2 border-top" : ""}>
                                                                            <div className="tooltip-item">
                                                                                <strong>Loan ID:</strong>
                                                                                <span>{loan.loanId}</span>
                                                                            </div>
                                                                            <div className="tooltip-item">
                                                                                <strong>Due Date:</strong>
                                                                                <span>{formatDate(loan.dueDate)}</span>
                                                                            </div>
                                                                            <div className="tooltip-item">
                                                                                <strong>Amount:</strong>
                                                                                <span>{formatCurrency(loan.dueAmount)}</span>
                                                                            </div>
                                                                            <div className="tooltip-item">
                                                                                <strong>Type:</strong>
                                                                                <span>{loan.type}</span>
                                                                            </div>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </span>
                                                </td>
                                                <td>
                                                    <ul className="action-btn">
                                                        <li onClick={() => handleEditDetails(record)} title="Edit Details">
                                                            <i className="ic-edit"></i>
                                                        </li>
                                                        <li onClick={() => handleViewDetails(record)} title="View Details">
                                                            <i className="ic-remove_red_eyevisibility"></i>
                                                        </li>
                                                        <li onClick={() => handleViewHistory(record)} title="Transaction History">
                                                            <i className="ic-Call-history"></i>
                                                        </li>
                                                        {(record.overallStatus === 1 || record.overallStatus === 2 || record.overallStatus === '1' || record.overallStatus === '2') && (
                                                            <li onClick={() => handleReconcile(record.id)} title="Reconcile">
                                                                <i className="ic-replay"></i>
                                                            </li>
                                                        )}
                                                    </ul>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>

                            {/* Pagination UI */}
                            <div className="pagination-wrapper mt-3">
                                <div className="pagination-info">
                                    Showing {data.length} of {pagination.totalrecords} records
                                </div>
                                <div className="pagination-controls pagination-bx">
                                    <ul className="pagination mb-0">
                                        <li className={`page-item ${!pagination.prevpage ? 'disabled' : ''}`}>
                                            <button className="page-link" onClick={() => handlePageChange(currentPage - 1)}>
                                                <i className="ic-chevron-left"></i> Previous
                                            </button>
                                        </li>
                                        {[...Array(pagination.totalpage)].map((_, i) => {
                                            const pageNum = i + 1;
                                            if (
                                                pageNum === 1 ||
                                                pageNum === pagination.totalpage ||
                                                (pageNum >= currentPage - 2 && pageNum <= currentPage + 2)
                                            ) {
                                                return (
                                                    <li key={pageNum} className={`page-item ${currentPage === pageNum ? 'active' : ''}`}>
                                                        <button className="page-link" onClick={() => handlePageChange(pageNum)}>
                                                            {pageNum}
                                                        </button>
                                                    </li>
                                                );
                                            } else if (
                                                (pageNum === currentPage - 3 && pageNum > 1) ||
                                                (pageNum === currentPage + 3 && pageNum < pagination.totalpage)
                                            ) {
                                                return <li key={pageNum} className="page-item disabled"><span className="page-link">...</span></li>;
                                            }
                                            return null;
                                        })}
                                        <li className={`page-item ${!pagination.nextpage ? 'disabled' : ''}`}>
                                            <button className="page-link" onClick={() => handlePageChange(currentPage + 1)}>
                                                Next <i className="ic-chevron-right"></i>
                                            </button>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Modal */}
            {showModal && selectedRecord && (
                <div className="model-outer bank-record-popup">
                    <Modal show={showModal} handleClose={hideModal}>
                        <div className="modal-header">
                            <div className='heading-txt-modal'>
                                <h2>{modalMode === 'edit' ? 'Bank Record' : 'View Details'} for {selectedRecord.dealer_name || selectedRecord.dealerName} (GCD- {selectedRecord.dealer_id || selectedRecord.dealerId})</h2>
                                <span className='sub-heading'>
                                     Proof-Id : {selectedRecord.id}
                                </span>
                            </div>
                        </div>

                        <Tab.Container id="left-tabs-example" defaultActiveKey="first">
                            <Nav variant="pills" className="flex-column tab-line nav nav-pills">
                                <div className="tab-list d-flex">
                                    <Nav.Item>
                                        <Nav.Link eventKey="first">Bank Statement</Nav.Link>
                                    </Nav.Item>
                                    <Nav.Item>
                                        <Nav.Link eventKey="second">Loan Assignment</Nav.Link>
                                    </Nav.Item>
                                </div>
                            </Nav>

                            <div className="modal-body">
                                <Tab.Content>
                                    <Tab.Pane eventKey="first">
                                        <div className='mtach-record-txt'>
                                            <span>Matched Record</span>
                                            <button className='matched-btn'>
                                                {getBankMatchLabel(selectedRecord.bank_match_status)}
                                            </button>
                                        </div>
                                        <div className='match-record-bg'>
                                            {selectedRecord?.bank_statement_system_match?.name || "Not found"}
                                        </div>
                                        <BankMatchRecordPopupList
                                            record={selectedRecord}
                                            onSuccess={handleActionSuccess}
                                            viewOnly={modalMode === 'view'}
                                        />
                                    </Tab.Pane>
                                    <Tab.Pane eventKey="second">
                                        <div className='mtach-record-txt'>
                                            <span>Loan ID: {selectedRecord.upload_proof_loan_id}</span>
                                            <button className='matched-btn'>
                                                {getLoanStatusLabel(selectedRecord.loan_assigned_status)}
                                            </button>
                                        </div>
                                        <LoanAssignmentRecordPopup
                                            record={selectedRecord}
                                            onSuccess={handleActionSuccess}
                                            viewOnly={modalMode === 'view'}
                                        />
                                    </Tab.Pane>
                                </Tab.Content>
                            </div>
                        </Tab.Container>
                    </Modal>
                </div>
            )}

            {/* Status History Modal */}
            {showHistoryModal && selectedRecord && (
                <div className="custom-modal-overlay">
                    <StatusHistoryPopup 
                        uploadProofId={selectedRecord.id} 
                        handleClose={hideModal} 
                    />
                </div>
            )}
        </React.Fragment>
    );
};

// Modal Component
const Modal = ({ handleClose, show, children }) => {
    const showHideClassName = show ? 'modal display-block' : 'modal display-none';

    return (
        <div className={showHideClassName}>
            <section className='modal-main'>
                {children}
                <button onClick={handleClose} className="close_icn">
                    <i className="ic-clearclose"></i>
                </button>
            </section>
        </div>
    );
};

export default ReconList;